import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, Observable } from 'rxjs';
import { DataAggregationService, SubmissionListByFilterPayload, SubmissionStatusResponse, UnderwriterSubmissionPayload, UnderwriterSubmissionResponse } from '../../../core/services/data-aggregation.service';



import {
  SubmissionTableLabAdapter,
  SubmissionTableLabListResponse,
  SUBMISSION_TABLE_LAB_ADAPTER
} from './submission-table-lab.adapter';
import { SubmissionTableLabRow } from './submission-table-lab.models';
import { AuthService } from '../../../core/auth/auth.service';
import { SubmissionProcessingStateService } from './submission-processing-state.service';

@Injectable({ providedIn: 'root' })
export class SubmissionTableLabAppAdapterService implements SubmissionTableLabAdapter {
  private readonly dataFactory = inject(DataAggregationService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly submissionProcessingState = inject(SubmissionProcessingStateService);
  private readonly openingSubmissionIds = signal<Record<string, boolean>>({});

  readonly transientSocketStatus = this.submissionProcessingState.transientSocketStatus;

  getUserId(): number | null {
    return this.auth.userId();
  }

  getCurrentUserUnderwriterFilter(): string {
    const user = this.auth.session()?.user;
    const firstName = user?.first_name?.trim() ?? '';
    const lastName = user?.last_name?.trim() ?? '';

    if (!firstName) {
      return '';
    }

    if (!lastName) {
      return firstName;
    }

    return `${firstName} ${lastName.charAt(0).toUpperCase()}`;
  }

  loadSubmissions(payload: UnderwriterSubmissionPayload): Observable<UnderwriterSubmissionResponse> {
    return this.dataFactory.loadSubmissions(payload);
  }

  listSubmissionsByFilter(payload: SubmissionListByFilterPayload): Observable<SubmissionTableLabListResponse> {
    return this.dataFactory.listSubmissionsByFilter(payload);
  }

  getSubmissionStatus(submissionId: string, quoteId: string): Observable<SubmissionStatusResponse> {
    return this.dataFactory.getSubmissionStatus(submissionId, quoteId);
  }

  getRowActionStatus(row: SubmissionTableLabRow): SubmissionTableLabRow['actionStatus'] {
    return this.submissionProcessingState.getRowActionStatus(row as never) as SubmissionTableLabRow['actionStatus'];
  }

  isProcessingSubmission(submissionId: string): boolean {
    return this.submissionProcessingState.processingSubmissionIds()[submissionId] === true;
  }

  isOpeningSubmission(submissionId: string): boolean {
    return this.openingSubmissionIds()[submissionId] === true;
  }

  getProcessingProgress(submissionId: string): number {
    const progress = this.submissionProcessingState.processingProgressBySubmissionId()[submissionId];
    return typeof progress === 'number' ? progress : 0;
  }

  openProcessedSubmission(row: SubmissionTableLabRow): void {
    this.openingSubmissionIds.update((state) => ({
      ...state,
      [row.id]: true
    }));
    // this.loader.show('Loading submission insights');

    const applicationTypesV2 = [495, 485, 499];
    const useV2Api = applicationTypesV2.includes(Number(row.applicationType));

    const request$ = useV2Api
      ? this.dataFactory.getRiskProfileDetails(row.id, row.applicationType)
      : this.dataFactory.getRiskProfileDetails492(row.id);

    request$
      .pipe(
        finalize(() => {
          this.openingSubmissionIds.update((state) => ({
            ...state,
            [row.id]: false
          }));
          // this.loader.reset();
        })
      )
      .subscribe({
        next: (response) => {
          const prefetchedInsightData = this.extractRiskProfileData(response);

          if (!prefetchedInsightData) {
            this.openSubmissionInsightsUnavailableModal(row, this.extractSubmissionNotFoundMessage(response));
            return;
          }

          void this.router.navigate(['/submissions', row.id], {
            queryParams: {
              quoteId: row.quoteId,
              applicationTypeId: row.applicationType,
              documentsReady: '1',
              sourceEntity: 'submissions'
            },
            state: {
              riskProfileData: prefetchedInsightData
            }
          });
        },
        error: () => {
          this.openSubmissionInsightsUnavailableModal(
            row,
            'We could not load the submission insights right now. Please try again in a moment.'
          );
        }
      });
  }

  startSubmissionProcessing(row: SubmissionTableLabRow): void {
    const userId = this.getUserId();

    if (!userId) {
      return;
    }

    this.submissionProcessingState.startSubmissionProcessing(row as never, userId);
  }

  private openSubmissionInsightsUnavailableModal(row: SubmissionTableLabRow, detail: string): void {
    // this.popupFactory.open(PopupConfirmContentComponent, {
    //   title: `Submission ${row.id} unavailable`,
    //   width: 'min(92vw, 520px)',
    //   maxWidth: '520px',
    //   showFooter: false,
    //   data: {
    //     message: 'We could not open this processed submission.',
    //     detail
    //   }
    // });
  }

  private extractRiskProfileData(response: unknown): Record<string, unknown> | null {
    if (!response || typeof response !== 'object' || Array.isArray(response)) {
      return null;
    }

    const record = response as Record<string, unknown>;
    const data = record['data'];

    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return data as Record<string, unknown>;
    }

    if (record['submission'] && typeof record['submission'] === 'object') {
      return record;
    }

    return null;
  }

  private extractSubmissionNotFoundMessage(response: unknown): string {
    if (!response || typeof response !== 'object' || Array.isArray(response)) {
      return 'The submission details are no longer available.';
    }

    const record = response as Record<string, unknown>;
    const message = typeof record['message'] === 'string' ? record['message'].trim() : '';

    if (message) {
      return message;
    }

    return 'The submission details are no longer available.';
  }
}

export const submissionTableLabAppAdapterProvider = {
  provide: SUBMISSION_TABLE_LAB_ADAPTER,
  useExisting: SubmissionTableLabAppAdapterService
};
